import { useEffect, useMemo, useState } from 'react';
import type { CausaleAssenza, DataISO, Evento, TipoEvento } from '../api/tipi.ts';
import type { EsitoVerifica, Verificatore } from '../api/verifica.ts';
import { ricordaTipoScelto, ultimoTipoScelto } from './ultimoTipo.ts';
import stili from './PannelloRichiesta.module.css';

/**
 * Richiesta di assenza.
 *
 * A tutta pagina sul telefono, laterale sul desktop. Non una tendina: per
 * scegliere un intervallo di date col pollice serve spazio, e un menu che si
 * chiude al primo tocco fuori e' il modo piu' rapido di perdere quello che si
 * stava compilando.
 *
 * Il modulo si adatta al tipo scelto, perche' e' il modello dati a imporlo:
 * un'assenza a ore vive in un giorno solo e vuole una causale, uno spostamento
 * vuole la destinazione, un riposo produzione non vuole nulla oltre alle date.
 *
 * Il preavviso viene verificato MENTRE si compila, chiedendolo all'API. Il
 * frontend non conosce le regole: le interroga.
 */

export interface ProprietaPannello {
  dipendenteId: string;
  /** Nome mostrato in testata: serve quando un delegato inserisce per altri. */
  nomeDipendente: string;
  /** Data preselezionata, quando si arriva da un clic sul calendario. */
  giornoIniziale?: DataISO;
  /**
   * Se offrire il tipo "Riposo produzione".
   *
   * Non e' un'assenza che si chiede ma produzione ferma, e la registra solo
   * chi ha una delega su una sede o un reparto. Arriva gia' deciso dal
   * profilo: il pannello non conosce le deleghe.
   */
  puoRiposoProduzione: boolean;
  /**
   * L'ultimo giorno prenotabile.
   *
   * Il 31 dicembre dell'anno corrente, o del successivo da luglio in poi. Il
   * campo data lo fa rispettare da subito invece di lasciar scegliere un
   * giorno che il server rifiuterebbe dopo aver premuto Invia.
   */
  ultimoGiornoInseribile: DataISO;
  /**
   * Il primo giorno scrivibile.
   *
   * Oggi per quasi tutti: il passato non si tocca. Per amministratori e
   * responsabili il primo gennaio dell'anno in corso, perche' registrare a
   * posteriori una malattia comunicata a voce e' parte del mestiere. Il
   * valore arriva dal server: rifare il conto qui darebbe un limite diverso
   * su un portatile con l'orologio sbagliato.
   */
  primoGiornoInseribile: DataISO;
  /**
   * Ore che vale una giornata intera per la persona a cui si riferisce la
   * richiesta: otto a tempo pieno, meno per un part-time.
   */
  /** Ore lavorate per giorno, da lunedi' a domenica. Zero = non lavora. */
  orarioSettimanale: number[];
  /**
   * Gli eventi gia' registrati della persona, non filtrati per giorno.
   *
   * Filtra il pannello, sul giorno che ha nel primo campo data. Passarli
   * gia' filtrati dall'esterno sembrava piu' semplice, ed era sbagliato:
   * l'elenco restava quello della cella cliccata anche dopo che l'utente
   * aveva cambiato la data, e il pulsante Annulla accanto a un evento agiva
   * su un giorno diverso da quello che si stava guardando.
   *
   * Il pannello si apre anche su una giornata piena - prima il click non
   * faceva niente, ed era proprio la cella che si voleva sistemare.
   */
  eventiDellaPersona?: readonly Evento[];
  /**
   * Annulla un evento gia' registrato. Assente = sola lettura.
   *
   * Non cancella: porta lo stato ad ANNULLATO, lascia una riga nel registro
   * con i valori di prima, e quella riga compare nel riepilogo del mattino
   * ai responsabili. Correggere il passato di qualcun altro e' un gesto che
   * si vede.
   */
  onAnnulla?: (evento: Evento) => void | Promise<void>;
  verifica: Verificatore;
  onChiudi: () => void;
  onInvia: (richiesta: DatiRichiesta) => void | Promise<void>;
}

/**
 * Gli eventi che occupano un certo giorno.
 *
 * Il confronto e' fra stringhe ISO, che per le date AAAA-MM-GG ordinano come
 * il calendario. Un evento di piu' giorni occupa anche quelli in mezzo: il
 * confronto e' sull'intervallo, non sulla data di inizio.
 */
export function eventiSulGiorno(
  eventi: readonly Evento[],
  giorno: DataISO,
): Evento[] {
  if (giorno === '') return [];
  return eventi.filter((e) => e.dataInizio <= giorno && e.dataFine >= giorno);
}

/** 2026-03-02 -> 02/03/2026, per un'intestazione che si legge. */
/**
 * L'ultimo giorno che si puo' chiedere come urgente.
 *
 * Il server ammette un giorno che COMINCIA entro 48 ore da adesso: a
 * qualunque ora del giorno, e' sempre dopodomani compreso e mai il giorno
 * dopo. Il campo data lo fa rispettare prima di premere Invia. La regola
 * vera resta sul server (FINESTRA_URGENTE_ORE in core/regole.ts): questo e'
 * solo il tetto del calendario.
 */
function ultimoGiornoUrgente(oggi: DataISO): DataISO {
  return new Date(Date.parse(`${oggi}T00:00:00Z`) + 2 * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Le ore lavorate in questa data da chi ha questo orario. Zero = non lavora.
 *
 * Stessa regola del server (oreDelGiorno in core/tempo.ts): qui serve solo a
 * dire il tetto giusto mentre si compila.
 */
function oreDelGiorno(orario: readonly number[], data: DataISO): number {
  const giorno = new Date(`${data}T00:00:00Z`).getUTCDay();
  return orario[giorno === 0 ? 6 : giorno - 1] ?? 0;
}

/** 7 invece di 7,00: le mezze ore esistono, gli zeri finali no. */
function arrotondaOre(ore: number): string {
  return String(Math.round(ore * 100) / 100).replace('.', ',');
}

function italiana(data: DataISO): string {
  return data.split('-').reverse().join('/');
}

/** Come si legge uno stato in mezzo a una frase, non in una pastiglia. */
const STATO_LEGGIBILE: Record<string, string> = {
  IN_ATTESA: 'in attesa',
  APPROVATO: 'approvata',
  REGISTRATO: 'registrata',
  RIFIUTATO: 'rifiutata',
};

export interface DatiRichiesta {
  tipo: TipoEvento;
  dataInizio: DataISO;
  dataFine: DataISO;
  ore: number | null;
  causale: CausaleAssenza | null;
  nota: string;
  urgente: boolean;
}

interface DefinizioneTipo {
  codice: TipoEvento;
  etichetta: string;
  descrizione: string;
  unGiornoSolo: boolean;
  vuoleOre: boolean;
  vuoleCausale: boolean;
  vuoleNota: boolean;
  ammetteUrgenza: boolean;
  /**
   * Se il tipo occupa giornate intere invece di ore dichiarate.
   *
   * Quante ore valga una giornata non lo decide il tipo ma la persona: un
   * part-time da sei ore ne consuma sei. Qui si registra solo che il conteggio
   * va a giornate.
   */
  giornataIntera?: boolean;
}

/**
 * Rispecchia la tabella `tipi_evento`. Quando l'API sara' disponibile questo
 * elenco arrivera' da `GET /api/tipi-evento` invece di stare qui: i metadati
 * che guidano il modulo sono gia' un dato, non una regola scritta due volte.
 */
const TIPI: DefinizioneTipo[] = [
  {
    codice: 'FERIE',
    etichetta: 'Ferie',
    descrizione: 'Uno o più giorni interi',
    unGiornoSolo: false,
    vuoleOre: false,
    vuoleCausale: false,
    vuoleNota: false,
    // Un giorno intero puo' essere urgente: da oggi a dopodomani, un giorno solo.
    ammetteUrgenza: true,
    giornataIntera: true,
  },
  {
    codice: 'ORE_FERIE',
    etichetta: 'Ore di assenza',
    descrizione: 'Ferie a ore, recupero o legge 104',
    unGiornoSolo: true,
    vuoleOre: true,
    vuoleCausale: true,
    vuoleNota: false,
    ammetteUrgenza: true,
  },
  {
    codice: 'SPOSTAMENTO',
    etichetta: 'Spostamento / trasferta',
    descrizione: 'La destinazione va nella nota',
    unGiornoSolo: true,
    vuoleOre: false,
    vuoleCausale: false,
    vuoleNota: true,
    ammetteUrgenza: false,
  },
  {
    codice: 'RIPOSO_PRODUZIONE',
    etichetta: 'Riposo produzione',
    descrizione: '8 ore al giorno, conteggiate a parte',
    unGiornoSolo: false,
    vuoleOre: false,
    vuoleCausale: false,
    vuoleNota: false,
    ammetteUrgenza: false,
    giornataIntera: true,
  },
];

const CAUSALI: { codice: CausaleAssenza; etichetta: string; nota?: string }[] = [
  { codice: 'FERIE', etichetta: 'Ore di ferie' },
  { codice: 'RECUPERO', etichetta: 'Recupero ore' },
  {
    codice: 'LEGGE_104',
    etichetta: 'Permesso legge 104',
    nota: 'Approvato subito: è un diritto, non passa da nessuno. I colleghi vedono solo “ASSENTE”.',
  },
  {
    codice: 'MF',
    etichetta: 'MF',
    nota: 'Approvato subito: è un diritto, non passa da nessuno. I colleghi vedono solo “ASSENTE”.',
  },
  {
    codice: 'CP',
    etichetta: 'Congedo paternità',
    nota: 'Approvato subito: è un diritto, non passa da nessuno. I colleghi vedono solo “ASSENTE”.',
  },
  {
    codice: 'DS',
    etichetta: 'Donazione sangue',
    nota: 'Approvata subito: è un diritto, non passa da nessuno. I colleghi vedono solo “ASSENTE”.',
  },
  {
    codice: 'MALATTIA',
    etichetta: 'Malattia',
    nota: 'Approvata subito: è un diritto, non passa da nessuno. I colleghi vedono solo “ASSENTE”.',
  },
];

/**
 * Le ore ammesse per causale, come nel dominio.
 *
 * Qui servono a proporre i valori giusti invece di lasciare scrivere un
 * numero qualsiasi e respingerlo dopo. La verifica vera resta sul server:
 * questa e' comodita', non sicurezza.
 */
const ORE_AMMESSE: Partial<Record<CausaleAssenza, number[]>> = {
  LEGGE_104: [4, 8],
  MF: [8],
  CP: [8],
  DS: [8],
  MALATTIA: [4, 8],
};

export function PannelloRichiesta({
  dipendenteId,
  nomeDipendente,
  giornoIniziale,
  puoRiposoProduzione,
  ultimoGiornoInseribile,
  primoGiornoInseribile,
  orarioSettimanale,
  eventiDellaPersona = [],
  onAnnulla,
  verifica,
  onChiudi,
  onInvia,
}: ProprietaPannello) {
  const oggi = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  const [inAnnullamento, setInAnnullamento] = useState<string | null>(null);
  // L'errore dell'annullamento sta accanto al pulsante che l'ha prodotto, non
  // in fondo al pannello: chi preme Annulla guarda li'.
  const [erroreAnnullamento, setErroreAnnullamento] = useState<string | null>(null);

  // Parte dall'ultimo tipo scelto in questa sessione e non sempre da Ferie:
  // chi inserisce una serie di spostamenti non deve riselezionarlo a ogni giorno.
  const [codiceTipo, setCodiceTipo] = useState<TipoEvento>(
    () =>
      ultimoTipoScelto(
        TIPI.filter((t) => t.codice !== 'RIPOSO_PRODUZIONE' || puoRiposoProduzione).map(
          (t) => t.codice,
        ),
      ) ?? 'FERIE',
  );
  const [dataInizio, setDataInizio] = useState<DataISO>(giornoIniziale ?? '');
  const [dataFine, setDataFine] = useState<DataISO>(giornoIniziale ?? '');
  const [ore, setOre] = useState('');
  const [causale, setCausale] = useState<CausaleAssenza>('FERIE');
  const [nota, setNota] = useState('');
  const [urgente, setUrgente] = useState(false);
  const [esito, setEsito] = useState<EsitoVerifica | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [invioInCorso, setInvioInCorso] = useState(false);
  // L'errore del salvataggio si mostra QUI, non in una barra in cima alla
  // pagina: chi ha appena premuto Invia deve leggerne il motivo dove stava
  // lavorando.
  const [erroreInvio, setErroreInvio] = useState<string | null>(null);

  // I tipi effettivamente offerti. Nascondere quello che il server
  // rifiuterebbe e' meglio che mostrarlo e spiegare dopo perche' no.
  const tipiDisponibili = useMemo(
    () => TIPI.filter((t) => t.codice !== 'RIPOSO_PRODUZIONE' || puoRiposoProduzione),
    [puoRiposoProduzione],
  );

  /*
   * Cosa c'e' gia' sul giorno che il modulo mostra adesso.
   *
   * Si guarda `dataInizio`, non il giorno da cui il pannello e' stato
   * aperto: cambiando data l'elenco cambia con lei, e l'intestazione dice
   * di che giorno si tratta. Prima l'elenco restava fermo al giorno
   * cliccato mentre il modulo ne mostrava un altro, e il pulsante Annulla
   * accanto agiva su quello vecchio senza dirlo.
   */
  const eventiDelGiorno = useMemo(
    () => eventiSulGiorno(eventiDellaPersona, dataInizio),
    [eventiDellaPersona, dataInizio],
  );

  const tipo = TIPI.find((t) => t.codice === codiceTipo)!;

  // Le ore del giorno scelto: chi lavora tre ore il sabato e otto il lunedi'
  // ha due tetti diversi, e il modulo deve dire quello giusto. Senza data
  // ancora scelta si mostra la giornata piu' lunga della settimana.
  const oreGiornata =
    dataInizio === ''
      ? Math.max(...orarioSettimanale)
      : oreDelGiorno(orarioSettimanale, dataInizio);

  // Un'urgente riguarda sempre un giorno solo, anche quando il tipo ne
  // ammetterebbe di piu': il campo "Al giorno" sparisce invece di lasciar
  // scegliere un intervallo che il server rifiuterebbe.
  const unGiornoSolo = tipo.unGiornoSolo || urgente;
  const ultimoUrgente = ultimoGiornoUrgente(oggi) < ultimoGiornoInseribile
    ? ultimoGiornoUrgente(oggi)
    : ultimoGiornoInseribile;

  // Cambiando tipo, i campi che non gli appartengono vanno azzerati: lasciarli
  // valorizzati significherebbe inviare ore su una richiesta di ferie.
  useEffect(() => {
    if (!tipo.ammetteUrgenza) setUrgente(false);
    if (!tipo.vuoleOre) setOre('');
  }, [tipo]);

  useEffect(() => {
    if (unGiornoSolo && dataInizio !== '') setDataFine(dataInizio);
  }, [unGiornoSolo, dataInizio]);

  // Cambiando causale, le ore fuori dai valori ammessi vengono riportate al
  // primo consentito: lasciarle avrebbe fatto premere Invia su una richiesta
  // che il server rifiuta, senza che si capisca perche'.
  useEffect(() => {
    if (!tipo.vuoleCausale) return;
    const ammesse = ORE_AMMESSE[causale];
    if (ammesse === undefined) return;
    const attuali = Number((ore || '').replace(',', '.'));
    if (!ammesse.includes(attuali)) setOre(String(ammesse[0]));
  }, [causale, tipo.vuoleCausale, ore]);

  useEffect(() => {
    if (dataInizio === '' || dataFine === '') {
      setEsito(null);
      return;
    }
    // Piccolo ritardo per non interrogare l'API a ogni tasto premuto.
    const attesa = setTimeout(() => {
      setInCorso(true);
      void verifica({
        dipendenteId,
        tipo: codiceTipo,
        dataInizio,
        dataFine: tipo.unGiornoSolo ? dataInizio : dataFine,
        ore: tipo.vuoleOre && ore !== '' ? Number(ore.replace(',', '.')) : null,
        causale: tipo.vuoleCausale ? causale : null,
        urgente,
      })
        .then(setEsito)
        .catch(() => setEsito(null))
        .finally(() => setInCorso(false));
    }, 250);
    return () => clearTimeout(attesa);
  }, [dipendenteId, codiceTipo, tipo, dataInizio, dataFine, ore, causale, urgente, verifica]);

  const oreAmmesse = tipo.vuoleCausale ? ORE_AMMESSE[causale] : undefined;
  const notaCausale = tipo.vuoleCausale
    ? CAUSALI.find((c) => c.codice === causale)?.nota
    : undefined;

  const completo =
    dataInizio !== '' &&
    (unGiornoSolo || dataFine !== '') &&
    (!tipo.vuoleOre || ore !== '') &&
    (!tipo.vuoleNota || nota.trim() !== '');
  const inviabile = completo && esito?.ammesso === true;

  return (
    <div className={stili.velo} role="dialog" aria-modal="true" aria-label="Richiesta di assenza">
      <div className={stili.pannello}>
        <header className={stili.testata}>
          <div>
            <h2 className={stili.titolo}>Richiedi assenza</h2>
            <p className={stili.sottotitolo}>{nomeDipendente}</p>
          </div>
          <button type="button" className={stili.chiudi} onClick={onChiudi} aria-label="Chiudi">
            ✕
          </button>
        </header>

        <div className={stili.contenuto}>
          {/*
            Cosa c'e' gia' su quel giorno.
            Il pannello si apre anche quando la giornata e' piena: prima il
            click non faceva niente, ed era esattamente la cella che si
            voleva correggere.
          */}
          {eventiDelGiorno.length > 0 && (
            <section className={stili.presenti}>
              <h3 className={stili.etichetta}>
                {eventiDelGiorno.length === 1 ? 'Già presente il ' : 'Già presenti il '}
                {italiana(dataInizio)}
              </h3>
              <ul className={stili.presentiElenco}>
                {eventiDelGiorno.map((evento) => (
                  <li key={evento.id} className={stili.presenteVoce}>
                    <span>
                      <strong>{evento.etichetta}</strong>
                      {evento.ore !== null && ` · ${evento.ore} h`}
                      {evento.nota !== null && evento.nota.trim() !== '' && ` · ${evento.nota}`}
                      <span className={stili.presenteStato}>
                        {STATO_LEGGIBILE[evento.stato] ?? evento.stato.toLowerCase()}
                      </span>
                    </span>
                    {onAnnulla !== undefined && (
                      <button
                        type="button"
                        className={stili.presenteAnnulla}
                        disabled={inAnnullamento !== null}
                        onClick={() => {
                          setInAnnullamento(evento.id);
                          setErroreAnnullamento(null);
                          void Promise.resolve(onAnnulla(evento))
                            .catch((e: unknown) => {
                              // Senza questo ramo un 409 - qualcun altro ha
                              // toccato l'evento nel frattempo - non si vedeva:
                              // il pulsante si riabilitava, la riga restava, e
                              // chi aveva premuto se ne andava convinto di
                              // averla annullata.
                              setErroreAnnullamento(
                                e instanceof Error
                                  ? e.message
                                  : "Annullamento non riuscito. Ricarica la pagina e riprova.",
                              );
                            })
                            .finally(() => setInAnnullamento(null));
                        }}
                      >
                        {inAnnullamento === evento.id ? 'Annullo…' : 'Annulla'}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {erroreAnnullamento !== null && (
                <p className={stili.riscontroErrore}>{erroreAnnullamento}</p>
              )}
            </section>
          )}

          <fieldset className={stili.gruppo}>
            <legend className={stili.etichetta}>Tipo</legend>
            <div className={stili.scelte}>
              {tipiDisponibili.map((t) => (
                <button
                  key={t.codice}
                  type="button"
                  className={`${stili.scelta} ${codiceTipo === t.codice ? stili.sceltaAttiva : ''}`}
                  onClick={() => {
                    setCodiceTipo(t.codice);
                    ricordaTipoScelto(t.codice);
                  }}
                  aria-pressed={codiceTipo === t.codice}
                >
                  <span className={stili.sceltaTitolo}>{t.etichetta}</span>
                  <span className={stili.sceltaNota}>{t.descrizione}</span>
                </button>
              ))}
            </div>
          </fieldset>

          <div className={unGiornoSolo ? stili.dataSingola : stili.date}>
            <label className={stili.campo}>
              <span className={stili.etichetta}>{unGiornoSolo ? 'Giorno' : 'Dal giorno'}</span>
              <input
                type="date"
                className={stili.input}
                value={dataInizio}
                // Un'urgente cade entro 48 ore: da oggi a dopodomani.
                min={urgente ? oggi : primoGiornoInseribile}
                max={urgente ? ultimoUrgente : ultimoGiornoInseribile}
                onChange={(e) => {
                  setDataInizio(e.target.value);
                  if (dataFine !== '' && e.target.value > dataFine) setDataFine(e.target.value);
                }}
              />
            </label>

            {!unGiornoSolo && (
              <label className={stili.campo}>
                <span className={stili.etichetta}>Al giorno</span>
                <input
                  type="date"
                  className={stili.input}
                  value={dataFine}
                  min={dataInizio || primoGiornoInseribile}
                  max={ultimoGiornoInseribile}
                  onChange={(e) => setDataFine(e.target.value)}
                />
              </label>
            )}
          </div>

          {tipo.vuoleCausale && (
            <label className={stili.campo}>
              <span className={stili.etichetta}>Causale</span>
              <select
                className={stili.input}
                value={causale}
                onChange={(e) => setCausale(e.target.value as CausaleAssenza)}
              >
                {CAUSALI.map((c) => (
                  <option key={c.codice} value={c.codice}>
                    {c.etichetta}
                  </option>
                ))}
              </select>
              {notaCausale && <span className={stili.suggerimento}>{notaCausale}</span>}
            </label>
          )}

          {tipo.vuoleOre && (
            <label className={stili.campo}>
              <span className={stili.etichetta}>Quante ore</span>
              {oreAmmesse === undefined ? (
                <>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={stili.input}
                    placeholder="es. 2 oppure 0,5"
                    value={ore}
                    onChange={(e) => setOre(e.target.value)}
                  />
                  {/* Il limite vero e' sul server (ORE_OLTRE_LA_GIORNATA):
                      qui si dice prima, invece di farlo scoprire dopo. */}
                  {causale === 'FERIE' && oreGiornata > 0 && (
                    <span className={stili.suggerimento}>
                      Al massimo {arrotondaOre(oreGiornata - 1)} ore. Per la giornata intera
                      scegli “Ferie”.
                    </span>
                  )}
                  {oreGiornata === 0 && (
                    <span className={stili.suggerimento}>
                      In questo giorno non si lavora: non ci sono ore da chiedere.
                    </span>
                  )}
                </>
              ) : (
                <>
                  {/* Con valori fissi un campo libero serve solo a sbagliare:
                      meglio scegliere fra quelli che esistono. */}
                  <select
                    className={stili.input}
                    value={ore}
                    onChange={(e) => setOre(e.target.value)}
                  >
                    {oreAmmesse.map((o) => (
                      <option key={o} value={String(o)}>
                        {o === 8 ? '8 ore (giornata intera)' : `${o} ore (mezza giornata)`}
                      </option>
                    ))}
                  </select>
                  <span className={stili.suggerimento}>
                    {oreAmmesse.length === 1
                      ? 'Questo permesso si prende solo a giornata intera.'
                      : 'Questo permesso si prende a mezza giornata o a giornata intera.'}
                  </span>
                </>
              )}
            </label>
          )}

          {tipo.giornataIntera === true && (
            <p className={stili.suggerimento}>
              Ogni giornata vale le ore lavorate quel giorno nel conteggio di fine mese.
            </p>
          )}

          <label className={stili.campo}>
            <span className={stili.etichetta}>
              {tipo.vuoleNota ? 'Destinazione' : 'Nota (facoltativa)'}
            </span>
            <input
              type="text"
              className={stili.input}
              placeholder={tipo.vuoleNota ? 'es. Modena, cliente Rossi' : 'es. esco alle 17'}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          </label>

          {tipo.ammetteUrgenza && (
            <label className={`${stili.interruttore} ${urgente ? stili.interruttoreAttivo : ''}`}>
              <input
                type="checkbox"
                className={stili.casella}
                checked={urgente}
                onChange={(e) => {
                  setUrgente(e.target.checked);
                  // Una data gia' scelta fuori dalla finestra va riportata
                  // dentro: lasciarla significherebbe un Invia che il server
                  // rifiuta senza che si capisca perche'.
                  if (e.target.checked && (dataInizio === '' || dataInizio < oggi || dataInizio > ultimoUrgente)) {
                    setDataInizio(oggi);
                  }
                }}
              />
              <span>
                <span className={stili.interruttoreTitolo}>Richiesta urgente</span>
                <span className={stili.interruttoreNota}>
                  Per imprevisti nelle prossime 48 ore: oggi, domani o dopodomani, un
                  giorno solo. Salta il preavviso ma resta in attesa finché un
                  responsabile non la decide, e i responsabili ricevono subito un’email.
                </span>
              </span>
            </label>
          )}

          <Riscontro esito={esito} inCorso={inCorso} completo={completo} urgente={urgente} />

          {erroreInvio && <p className={stili.riscontroErrore}>{erroreInvio}</p>}
        </div>

        <footer className={stili.piede}>
          <button type="button" className={stili.secondario} onClick={onChiudi}>
            Annulla
          </button>
          <button
            type="button"
            className={stili.primario}
            disabled={!inviabile || invioInCorso}
            onClick={async () => {
              setErroreInvio(null);
              setInvioInCorso(true);
              try {
                await onInvia({
                  tipo: codiceTipo,
                  dataInizio,
                  dataFine: tipo.unGiornoSolo ? dataInizio : dataFine,
                  ore: tipo.vuoleOre && ore !== '' ? Number(ore.replace(',', '.')) : null,
                  causale: tipo.vuoleCausale ? causale : null,
                  nota,
                  urgente,
                });
              } catch (e) {
                setErroreInvio(
                  e instanceof Error ? e.message : 'Salvataggio non riuscito.',
                );
              } finally {
                setInvioInCorso(false);
              }
            }}
          >
            {invioInCorso ? 'Invio in corso…' : 'Invia richiesta'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Riscontro({
  esito,
  inCorso,
  completo,
  urgente,
}: {
  esito: EsitoVerifica | null;
  inCorso: boolean;
  completo: boolean;
  urgente: boolean;
}) {
  if (!completo) {
    return <p className={stili.riscontroNeutro}>Compila i campi per vedere se è richiedibile.</p>;
  }
  if (inCorso || !esito) {
    return <p className={stili.riscontroNeutro}>Verifica in corso…</p>;
  }

  if (!esito.ammesso) {
    return (
      <div className={stili.riscontroErrore}>
        {esito.violazioni.map((v) => (
          <p key={v.errore} className={stili.riscontroRiga}>
            {v.messaggio}
          </p>
        ))}
      </div>
    );
  }

  return (
    <div className={stili.riscontroOk}>
      <p className={stili.riscontroRiga}>
        <strong>Richiedibile.</strong>{' '}
        {esito.giorniLavorativi === 1
          ? '1 giorno di assenza.'
          : `${esito.giorniLavorativi} giorni di assenza.`}
      </p>
      {urgente ? (
        <p className={stili.riscontroRiga}>
          Trattandosi di una richiesta urgente non viene approvata automaticamente: resta
          in attesa di un responsabile.
        </p>
      ) : (
        esito.approvazionePrevista && (
          <p className={stili.riscontroRiga}>
            Approvazione automatica prevista entro il{' '}
            {esito.approvazionePrevista.split('-').reverse().join('/')}.
          </p>
        )
      )}
      {esito.avvisi.map((a) => (
        <p key={a.codice} className={stili.riscontroAvviso}>
          {a.messaggio}
        </p>
      ))}
    </div>
  );
}
